<template>
  <div class="permission-tree-node" :class="{ 'is-root': isRoot }">
    <div class="node-header" :class="{ 'highlighted-gate': isHighlighted }" :style="{ paddingLeft: level ? `${level * 20}px` : '8px' }" @click="toggleExpanded">
      <!-- Expand/Collapse Icon -->
      <div class="expand-icon" :class="{ expanded: isExpanded, 'has-children': hasChildren }">
        <span v-if="hasChildren">{{ isExpanded ? '▼' : '▶' }}</span>
      </div>

      <!-- Node Type Icon -->
      <div class="node-icon">
        <span v-if="permissionGate.type === 'shopAll'">🏪</span>
        <span v-else-if="permissionGate.type === 'shopBasic'">⚙️</span>
        <span v-else-if="permissionGate.type === 'shopBasicClose'">🚫</span>
        <span v-else-if="permissionGate.type === 'shopPlan'">📋</span>
        <span v-else-if="permissionGate.type === 'plan'">📦</span>
        <span v-else-if="permissionGate.type === 'role'">👤</span>
        <span v-else-if="permissionGate.type === 'moduleSetting'">⚡</span>
        <span v-else-if="permissionGate.type === 'customPlan'">🎯</span>
        <span v-else>📄</span>
      </div>

      <!-- Node Information -->
      <div class="node-info">
        <div class="node-name">{{ permissionGate.name }}</div>
        <div class="node-details">
          <span class="node-type">{{ permissionGate.type }}</span>
          <span class="access-count">
            {{ accessCount }} 個權限項目
          </span>
        </div>
      </div>
    </div>

    <!-- Children -->
    <div v-if="isExpanded && hasChildren" class="node-children">
      <!-- 保持原始順序，依序渲染所有子項目 -->
      <template v-for="(access, index) in permissionGate.hasAccess">
        <!-- Permission Gate Child -->
        <PermissionTreeNode
          v-if="access.type === 'PermissionGate'"
          :key="`gate-${index}`"
          :permission-gate="access.permissionGate"
          :level="level + 1"
          :is-root="false"
          :highlight-feature-key="highlightFeatureKey"
          :highlighted-gates="highlightedGates"
        />

        <!-- Feature Key Child -->
        <div
          v-else-if="access.type === 'FeatureKey'"
          :key="`feature-${index}`"
          class="feature-key-item"
          :style="{ paddingLeft: `${(level + 1) * 20 + 40}px` }"
          :class="{
            'effect-allow': access.effect === 'allow',
            'effect-deny': access.effect === 'deny',
            'highlighted-feature': shouldHighlightFeature(access.key)
          }"
          @click="onQueryFeature(access.key)"
        >
          <div class="feature-icon">
            <span v-if="access.effect === 'allow'">✅</span>
            <span v-else>❌</span>
          </div>
          <div class="feature-info">
            <div class="feature-key">{{ access.key }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, inject } from 'vue'

export default defineComponent({
  name: 'PermissionTreeNode',
  props: {
    permissionGate: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      default: 0,
    },
    isRoot: {
      type: Boolean,
      default: false,
    },
    highlightFeatureKey: {
      type: String,
      default: '',
    },
    highlightedGates: {
      type: Array,
      default: () => [],
    },
  },
  setup (props) {
    // 使用 inject 獲取父組件提供的 onQueryFeature 方法
    const parentOnQueryFeature = inject('onQueryFeature', null)
    const isExpanded = ref(props.isRoot) // Root nodes are expanded by default

    const hasChildren = computed(() => {
      return props.permissionGate.hasAccess && props.permissionGate.hasAccess.length > 0
    })

    const accessCount = computed(() => {
      return props.permissionGate.hasAccess ? props.permissionGate.hasAccess.length : 0
    })

    const toggleExpanded = () => {
      if (hasChildren.value) {
        isExpanded.value = !isExpanded.value
      }
    }

    // 高亮相關的 computed properties
    const isHighlighted = computed(() => {
      return props.highlightedGates.some(gate => gate.name === props.permissionGate.name)
    })

    const shouldHighlightFeature = (featureKey) => {
      if (!props.highlightFeatureKey) return false

      // 直接匹配
      if (featureKey === props.highlightFeatureKey) return true

      // 通配符匹配
      if (featureKey.endsWith('*')) {
        const prefix = featureKey.slice(0, -1)
        return props.highlightFeatureKey.startsWith(prefix)
      }

      return false
    }

    // 查詢 featureKey 功能
    const onQueryFeature = (featureKey) => {
      console.log('node onQueryFeature', featureKey)
      if (parentOnQueryFeature) {
        parentOnQueryFeature(featureKey)
      }
    }

    // 自動展開功能
    const expandToGate = (targetGate) => {
      // 檢查是否需要展開當前節點
      const shouldExpand = isGateInPath(targetGate, props.permissionGate)

      if (shouldExpand && !isExpanded.value && hasChildren.value) {
        isExpanded.value = true
      }
    }

    const isGateInPath = (targetGate, currentGate) => {
      // 檢查目標關口是否在當前關口的子樹中
      if (currentGate.name === targetGate.name) {
        return true
      }

      if (!currentGate.hasAccess) return false

      for (const access of currentGate.hasAccess) {
        if (access.type === 'PermissionGate') {
          if (isGateInPath(targetGate, access.permissionGate)) {
            return true
          }
        }
      }

      return false
    }

    // 監聽自動展開事件
    const handleExpandEvent = (event) => {
      expandToGate(event.detail.targetGate)
    }

    // 監聽關閉所有節點事件
    const handleCollapseAllEvent = () => {
      // 除了根節點，其他所有節點都收起
      if (!props.isRoot) {
        isExpanded.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('expandToGate', handleExpandEvent)
      document.addEventListener('collapseAllNodes', handleCollapseAllEvent)
    })

    onUnmounted(() => {
      document.removeEventListener('expandToGate', handleExpandEvent)
      document.removeEventListener('collapseAllNodes', handleCollapseAllEvent)
    })

    return {
      isExpanded,
      hasChildren,
      accessCount,
      toggleExpanded,
      isHighlighted,
      shouldHighlightFeature,
      onQueryFeature,
    }
  },
})
</script>

<style lang="postcss" scoped>
.permission-tree-node {
  @apply mb-1;
}

.permission-tree-node.is-root {
  @apply border-2 border-gray-600 rounded-lg bg-gray-800 overflow-hidden;
}

.node-header {
  @apply flex items-center px-[12px] py-2 cursor-pointer transition-colors text-white;
  min-height: 48px;
}

.node-header:hover {
  @apply bg-gray-700;
}

.is-root .node-header {
  @apply bg-gray-700 font-semibold;
}

.is-root .node-header:hover {
  @apply bg-gray-600;
}

.expand-icon {
  @apply w-5 h-5 flex items-center justify-center mr-2 text-sm text-gray-300 transition-transform;
}

.expand-icon.expanded {
  @apply rotate-0;
}

/* fallback rotation for collapsed state */
.expand-icon:not(.expanded) {
  transform: rotate(-90deg);
}

.expand-icon:not(.has-children) {
  visibility: hidden;
}

.node-icon {
  @apply w-6 h-6 flex items-center justify-center mr-3 text-lg;
}

.node-info {
  @apply flex-1 min-w-0;
}

.node-name {
  @apply text-sm font-medium text-white mb-0.5 break-words;
}

.is-root .node-name {
  @apply text-base font-semibold;
}

.node-details {
  @apply flex gap-3 items-center flex-wrap;
}

.node-type {
  @apply bg-blue-600 text-white px-2 py-0.5 rounded text-xs uppercase font-medium;
}

.access-count {
  @apply text-xs text-gray-400;
}

.node-children {
  @apply border-t border-gray-600;
}

.feature-key-item {
  @apply flex items-center py-1.5 px-3 border-b border-gray-600 transition-colors bg-gray-800 cursor-pointer;
}

.feature-key-item:last-child {
  border-bottom: none;
}

.feature-key-item:hover {
  @apply bg-gray-700;
}

/* Use border-left utility (4px) as approximation for 3px in original design */
.feature-key-item.effect-allow {
  @apply border-l-4 border-green-500;
}

.feature-key-item.effect-deny {
  @apply border-l-4 border-red-500;
}

.feature-icon {
  @apply w-5 h-5 flex items-center justify-center mr-3 text-base;
}

.feature-info {
  @apply flex-1 min-w-0;
}

.feature-key {
  @apply font-mono text-xs text-gray-300 break-words mb-0.5;
}

.feature-effect {
  @apply text-xs font-medium uppercase;
}

.effect-allow .feature-effect {
  @apply text-green-400;
}

.effect-deny .feature-effect {
  @apply text-red-400;
}

/* 高亮樣式 */
.highlighted-gate .node-header {
  @apply bg-green-900 border-l-4 border-green-500;
}

.highlighted-gate .node-name {
  @apply text-[#58f993] font-bold;
}

.highlighted-feature {
  @apply bg-green-900 border-green-500;
}

.highlighted-feature .feature-key {
  @apply font-bold text-[#58f993];
}

/* 捲動高亮效果 */
.scroll-highlight {
  @apply animate-pulse bg-blue-900 border-2 border-blue-500 rounded-lg;
  animation: scrollHighlight 2s ease-in-out;
}

@keyframes scrollHighlight {
  0% {
    @apply bg-blue-800 border-blue-400;
  }
  50% {
    @apply bg-blue-900 border-blue-500;
  }
  100% {
    @apply bg-transparent border-transparent;
  }
}
</style>
